import React, { useState, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import { useStore, useSelector, useDispatch } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import MenuItem from "@material-ui/core/MenuItem";
import { type GridFilterModel, type GridSortModel } from "@material-ui/x-grid";

import {
  filteredExpensesSelector,
  mappedTransactionSelector,
} from "src/store/transaction/selector";
import { getAllSales } from "src/store/sale/selector";
import TimeFilter, {
  Select,
  getPeriodFromPreset,
} from "src/components/TimeFilter";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import { calendarYmdInTimeZone } from "src/utils/timezone";
import { useDateRange } from "src/utils/timeFilter";

import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import { type GridSelectionModel } from "@material-ui/x-grid";
import ClearIcon from "@material-ui/icons/Clear";

import TableHeader, { TableHeaderButton } from "src/components/TableHeader";
import XGrid from "src/components/ConfiguredXGrid";
import CSVFormattedXGrid from "src/components/CSVFormattedXGrid";
import {
  createTableToolbar,
  DefaultFeatures,
} from "src/components/TableToolbar";
import ConfirmDialog from "src/components/ConfirmDialog";
import InfoTooltip from "src/components/InfoTooltip";
import { StatefulLoadableButton } from "src/components/LoadableButton";
import ConditionalTooltip from "src/components/ConditionalTooltip";
import { valueGetterWithDefault } from "src/utils/valueGetter";
import vendorRenderCell from "src/utils/vendorRenderCell";
import renderHeaderDescriptionLabel from "src/utils/renderHeaderLabel";
import { useFullscreen } from "src/utils/fullscreen";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import { setTransactionsReviewed } from "src/apiService/modules/transactions";
import { setActiveDialog } from "src/store/adminHtml/actions";
import makeRenderCell from "src/utils/makeRenderCell";
import { formatAbsCurrency } from "src/utils/formatter";
import { makeTransactionsStyle } from "src/utils/gridCellStyles";
import { deleteMappedTransactions } from "src/apiService/modules/mapped-transactions";
import type { MappedTransaction } from "src/interfaces/transaction.interface";
import { withGridStringOperatorsWithNotContains } from "src/utils/gridStringFilters";
import {
  isSplitTransaction,
  notifySkippedSplitTransactions,
} from "../splitTransactions";

import { Columns as TransactionColumns } from "../All";

import BulkEditDialog from "./BulkEditDialog";
import BulkEditPanel from "./BulkEditPanel";

const TableToolbar = createTableToolbar({
  features: {
    ...DefaultFeatures,
    Fullscreen: true,
  },
  options: { fileName: "My Reseller Genie - Expenses" },
});

const TableColumnInfo = {
  description: "A note to remind you what was purchased.",
  integration_name: "The card or account used to make the purchase.",
  account:
    "The general ledger account is a tag that categorizes an expense. This is used to build accounting reports like the Profit & Loss Statement.",
};
const SelectedOrderTooltip =
  "Please remove the Order filter before using these filters";

const renderHeaderLabel = (params) => {
  const tooltip = TableColumnInfo[params.field];
  return (
    <div className="MuiDataGrid-columnHeaderTitle">
      {params.colDef.headerName}
      {!!tooltip && <InfoTooltip size="small" text={tooltip} />}
    </div>
  );
};

export const Columns = withGridStringOperatorsWithNotContains([
  {
    field: "id",
    hide: true,
  },
  {
    field: "account",
    headerName: "General Ledger Account",
    width: 250,
    renderHeader: renderHeaderLabel,
  },
  {
    field: "name",
    headerName: "Charge Description",
    width: 200,
    type: "string",
  },
  {
    field: "amount",
    headerName: "Amount",
    width: 200,
    type: "number",
    renderCell: makeRenderCell({
      valueFormatter: formatAbsCurrency,
      style: makeTransactionsStyle,
    }),
  },
  {
    field: "vendor",
    headerName: "Vendor",
    width: 200,
    renderCell: vendorRenderCell,
    renderHeader: renderHeaderDescriptionLabel,
    description: "The vendor is where you purchased the item (ex. Goodwill).",
    valueGetter: (params) => params.row.vendorName || params.value || "",
  },
  { field: "date", headerName: "Date", width: 200, type: "date" },
  {
    field: "description",
    headerName: "Description",
    width: 250,
    renderHeader: renderHeaderLabel,
  },
  {
    field: "integration_name",
    headerName: "Bank Account",
    width: 200,
    valueGetter: valueGetterWithDefault(""),
    renderHeader: renderHeaderLabel,
  },
]);

const useStyles = makeStyles((theme) => ({
  button: {
    whiteSpace: "nowrap",
    margin: theme.spacing(2, 1, 1, 0),
  },
}));

function Expenses() {
  const recordEvent = useRecordEvent();

  const dispatch = useDispatch();
  const classes = useStyles();
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const { timeOption: timeFilter, setDateRange } = useDateRange("transaction");
  const location = useLocation();
  const locationSearch = location?.search || "";
  const initialViewParam = new URLSearchParams(locationSearch).get("view");
  const initialView =
    initialViewParam === "all" ||
    initialViewParam === "unreviewed" ||
    initialViewParam === "expenses-on-sales" ||
    initialViewParam === "separate-expenses"
      ? initialViewParam
      : "separate-expenses";
  const [view, setView] = useState(initialView);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const tableSectionRef = useRef<HTMLDivElement>();
  const requestFullscreen = useFullscreen();
  const history = useHistory();
  const store = useStore();
  const [filters, orderFilter] = useMemo(() => {
    const models: GridFilterModel[] = [];
    if (locationSearch) {
      const orderId = new URLSearchParams(locationSearch).get("order-id");

      if (orderId) {
        const sale = getAllSales(store.getState()).find(
          (sale) => sale.transaction_id === orderId
        );

        return [
          [
            {
              items: [
                {
                  columnField: "orderId",
                  operatorValue: "equals",
                  value: orderId,
                },
              ],
            },
          ],
          sale?.item_title || orderId,
        ];
      }
    }

    const period =
      timeFilter.type === "custom"
        ? timeFilter.period
        : getPeriodFromPreset(timeFilter, timeZone);
    const model = {
      items: [
        {
          columnField: "date",
          operatorValue: "onOrAfter",
          value: calendarYmdInTimeZone(period.start, timeZone),
        },
        {
          columnField: "date",
          operatorValue: "onOrBefore",
          value: calendarYmdInTimeZone(period.end, timeZone),
        },
      ],
    };

    if (view !== "unreviewed" && view !== "separate-expenses") {
      model.items.push({
        columnField: "type",
        operatorValue: "equals",
        value: "expenses",
      });
    }

    switch (view) {
      case "unreviewed":
        model.items.push({
          columnField: "reviewed",
          operatorValue: "isStrict",
          value: "false",
        });
        break;
      case "expenses-on-sales":
        model.items.push({
          columnField: "collection",
          operatorValue: "equals",
          value: "sale",
        });
        break;
      case "separate-expenses":
        model.items.push({
          columnField: "type",
          operatorValue: "notEquals",
          value: "sale",
        });
        break;
    }

    models.push(model);
    return [models, undefined];
  }, [view, timeFilter, locationSearch, store, timeZone]);

  const transactionsCollection =
    view === "unreviewed" || view === "separate-expenses" || orderFilter;
  const _expenses = useSelector((state) =>
    transactionsCollection
      ? filteredExpensesSelector(state, filters)
      : mappedTransactionSelector(state, filters)
  );
  const expenses = useMemo(() => {
    if (transactionsCollection)
      return _expenses.filter((s) => s.accountDoc?.type?.[0] === "expenses");
    return _expenses;
  }, [_expenses, transactionsCollection]);

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "date", sort: "desc" },
  ]);
  const [dialog, setDialog] = useState<
    | {
        type: "delete";
        items: Pick<MappedTransaction, "collection" | "collection_id">[];
        skippedSplit?: number;
      }
    | { type: "bulk-edit" }
    | { type: "bulk-edit-panel" }
  >();

  const salesExpensesPresent = view === "all" || view === "expenses-on-sales";

  const { editableTransactionIds, readOnlyCount } = useMemo(() => {
    const ids: string[] = [];
    let readOnly = 0;
    for (const id of selectionModel) {
      const row = expenses.find((e) => e.id === id);
      if (!row) continue;
      if (row.read_only) {
        readOnly += 1;
      } else {
        ids.push(String(id));
      }
    }
    return { editableTransactionIds: ids, readOnlyCount: readOnly };
  }, [selectionModel, expenses]);

  return (
    <div>
      <TimeFilter
        value={timeFilter}
        onChange={(v) => setDateRange(v)}
        disabled={!!orderFilter}
        endChildren={
          orderFilter ? (
            <Button
              onClick={() => {
                const location = history.location;
                const searchParams = new URLSearchParams(locationSearch);
                searchParams.delete("order-id");
                const url = `${location.pathname}?${searchParams.toString()}${
                  location.hash || ""
                }`;
                history.push(url);
              }}
              variant="contained"
              className={classes.button}
              endIcon={<ClearIcon />}
            >
              Order: {orderFilter}
            </Button>
          ) : null
        }
        tooltip={orderFilter ? SelectedOrderTooltip : undefined}
      >
        <ConditionalTooltip
          wrap
          condition={!!orderFilter}
          title={SelectedOrderTooltip}
        >
          <Select
            disabled={!!orderFilter}
            label="View"
            value={view}
            onChange={(e) => {
              setSelectionModel([]);
              const view = e.target.value as string;
              setView(view);
              setSelectionModel([]);
              recordEvent({
                type: "feature-adoption",
                feature: "transaction-details-view",
                element: "expenses",
                view: view,
              });
            }}
          >
            <MenuItem value="all">All Transactions</MenuItem>
            <MenuItem value="unreviewed">Unreviewed</MenuItem>
            <MenuItem value="expenses-on-sales">Expenses on Sales</MenuItem>
            <MenuItem value="separate-expenses">Separate Expenses</MenuItem>
          </Select>
        </ConditionalTooltip>
      </TimeFilter>
      <Box
        component={Paper}
        {...({ ref: tableSectionRef } as any)}
        display="flex"
        flexDirection="column"
        minHeight={730}
        height="auto"
        p={2.5}
      >
        <TableHeader title="Expense Detail" hideChildrenInFullscreen>
          {view !== "all" ? (
            <>
              <TableHeaderButton
                color="primary"
                onClick={() => {
                  setDialog({ type: "bulk-edit-panel" });
                }}
                disabled={
                  salesExpensesPresent ||
                  selectionModel.length === 0 ||
                  editableTransactionIds.length === 0
                }
                tooltip={
                  salesExpensesPresent
                    ? 'To use this functionality, select the "Separate Expenses" view.'
                    : selectionModel.length === 0
                    ? "Select transactions to bulk edit."
                    : editableTransactionIds.length === 0
                    ? "Beginning Balance journals can't be bulk edited."
                    : undefined
                }
              >
                Bulk Edit Transactions
              </TableHeaderButton>
              <TableHeaderButton
                Component={StatefulLoadableButton as any}
                color="primary"
                disabled={
                  view === "expenses-on-sales" ||
                  !selectionModel.some(
                    (id) =>
                      expenses.find((e) => e.id === id)?.reviewed === false
                  )
                }
                tooltip={
                  view === "expenses-on-sales"
                    ? 'To use this functionality, please select a view other than "Expenses on Sale".'
                    : undefined
                }
                onClick={async () => {
                  const transactionIds = selectionModel.reduce((ti, id) => {
                    const expense = expenses.find((e) => e.id === id);
                    if (expense?.reviewed === false) ti.push(`${id}`);
                    return ti;
                  }, [] as string[]);
                  if (transactionIds.length > 0)
                    await setTransactionsReviewed(transactionIds);
                  setSelectionModel([]);
                  recordEvent({
                    type: "feature-adoption",
                    feature: "mark-reviewed-button",
                  });
                }}
              >
                Mark Reviewed
              </TableHeaderButton>
            </>
          ) : null}
          {view === "all" || view === "expenses-on-sales" ? null : (
            <TableHeaderButton
              colorType="error"
              disabled={selectionModel.length === 0}
              onClick={() => {
                let skippedSplit = 0;
                const items = selectionModel.reduce((all, id) => {
                  const item = expenses.find((r) => r.id === id);
                  if (item) {
                    // Split transactions can't be bulk deleted; they must be
                    // edited/deleted individually so the whole group is handled.
                    if (isSplitTransaction(item)) {
                      skippedSplit += 1;
                      return all;
                    }
                    const saleId = (item as any).sale_id;
                    if (saleId) {
                      all.push({
                        collection: "sale",
                        collection_id: saleId,
                      });
                    } else {
                      all.push({
                        collection: item.collection || "transaction",
                        collection_id: item.collection_id || item.id,
                      });
                    }
                  }
                  return all;
                }, [] as Pick<MappedTransaction, "collection_id" | "collection">[]);

                if (items.length === 0) {
                  notifySkippedSplitTransactions(skippedSplit);
                  return;
                }

                setDialog({ type: "delete", items, skippedSplit });
              }}
            >
              Delete Selected
            </TableHeaderButton>
          )}
        </TableHeader>

        <XGrid
          checkboxSelection
          tableName={
            transactionsCollection ? "transaction" : "mapped-transaction"
          }
          Component={CSVFormattedXGrid}
          rows={expenses}
          columns={transactionsCollection ? Columns : TransactionColumns}
          headerHeight={35}
          rowHeight={35}
          components={{
            Toolbar: TableToolbar,
          }}
          componentsProps={{
            toolbar: {
              onFullscreenClick: () => {
                if (document.fullscreenElement) {
                  document.exitFullscreen();
                  return;
                }
                if (!tableSectionRef.current) return;
                requestFullscreen(tableSectionRef.current);
                recordEvent({
                  type: "feature-adoption",
                  feature: "fullscreen",
                  element: "expenses-table",
                });
              },
              onQuickFilterBlur: (e) => {
                const text = e.target.value;
                if (!text) return;
                recordEvent({
                  type: "feature-adoption",
                  feature: "quick-filter",
                  element: "expenses",
                  text,
                });
              },
            },
          }}
          selectionModel={selectionModel}
          onSelectionModelChange={(selectionModel) =>
            setSelectionModel(selectionModel)
          }
          onCellDoubleClick={(e) => {
            if (e.row?.journal_entry_id) {
              dispatch(
                setActiveDialog("add_journal", {
                  id: e.row.journal_entry_id,
                })
              );
              return;
            }
            if (e.row?.split_group_id) {
              dispatch(
                setActiveDialog("edit_split", {
                  split_group_id: e.row.split_group_id,
                  id: e.row.collection_id || e.row.id,
                })
              );
              return;
            }
            if (e.row.collection === "transaction") {
              dispatch(
                setActiveDialog("edit_transaction", {
                  id: e.row.collection_id,
                })
              );
              return;
            }
            if (e.row.collection === "inventory") {
              dispatch(
                setActiveDialog("edit_inventory", {
                  id: e.row.collection_id,
                })
              );
              return;
            }
            if (e.row.collection === "sale") {
              dispatch(
                setActiveDialog("edit_sale", {
                  id: e.row.collection_id,
                })
              );
              return;
            }
            if (e.row?.type === "sale") {
              dispatch(
                setActiveDialog("edit_sale", {
                  id: e.row.sale_id,
                })
              );
              return;
            }
            dispatch(
              setActiveDialog("edit_transaction", {
                id: e.row.id,
              })
            );
          }}
          onFilterModelChange={(model) => {
            recordEvent({
              type: "feature-adoption",
              feature: "table-filter",
              element: "expenses",
              filter: JSON.stringify(model),
            });
          }}
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
        />
      </Box>
      <ConfirmDialog
        open={dialog?.type === "delete"}
        title="Confirm"
        text={
          dialog?.type === "delete"
            ? `Are you sure you want to delete ${dialog.items.length} transaction(s)?`
            : ""
        }
        onCancel={() => {
          setDialog(undefined);
        }}
        onConfirm={() => {
          if (dialog?.type !== "delete") return;
          deleteMappedTransactions(dialog.items);
          toast.success("Transaction(s) deleted successfully!");
          notifySkippedSplitTransactions(dialog.skippedSplit ?? 0);
          setDialog(undefined);
        }}
      />
      <BulkEditDialog
        open={dialog?.type === "bulk-edit"}
        onClose={() => setDialog(undefined)}
      />
      <BulkEditPanel
        open={dialog?.type === "bulk-edit-panel"}
        onClose={() => setDialog(undefined)}
        selectedTransactionIds={editableTransactionIds}
        readOnlyCount={readOnlyCount}
        onOpenCSVBulkEdit={() => setDialog({ type: "bulk-edit" })}
      />
    </div>
  );
}

export default Expenses;
